import metaModel from '~/shared/src/utilities/metaModel';
import Item, {ItemAttributesSettable} from '~/shared/src/ci/Item';

import {DATA_DELETE_ITEM} from '~/frontend/src/constants';
import {
    AppDispatch,
    StoreState,
    Thunk
} from '~/frontend/src/store';
import {setElusiveData} from '~/frontend/src/actions/elusiveData';
import deleteReference from '~/frontend/src/actions/data/deleteReference';


/**
 * deleteItem Action
 *
 * @memberOf! Actions
 *
 * @param {string} dataContext
 * @param {number} itemId
 * @param {boolean} [deletePhysically=true]
 * @param {string[]} [additionalElusiveDataContexts=[]]
 * @param {boolean} [deleteProperties=false]
 * @param {boolean} [deleteReferences=false] delete references of item (if referenced item is a new one, delete it also recursively)
 *
 * @return {void}
 */
const deleteItem = ( dataContext : string, itemId : number, deletePhysically : boolean = true, additionalElusiveDataContexts : string[] = [], deleteProperties : boolean = false, deleteReferences : boolean = false, chainReferenceDelete : boolean = true ) : Thunk<void> => ( dispatch : AppDispatch, getState : () => StoreState ) => {
    dispatch( {
        type: DATA_DELETE_ITEM,
        dataContext,
        itemId,
        deletePhysically,
        additionalElusiveDataContexts,
        deleteProperties
    } );

    let item : Item = global.data[ dataContext ]?.items[ itemId ];

    if ( item ) {

        if ( deleteReferences ) {

            // delete all references of the item
            for ( const referenceId of [ ...item.references ] ) {
                const reference   = global.data[ dataContext ]?.references[ referenceId ],
                      otherItemId = reference.srcItemId === item.id ? reference.destItemId : reference.srcItemId;

                dispatch( deleteReference(
                    dataContext,
                    reference.id,
                    deletePhysically,
                    additionalElusiveDataContexts,
                    deleteProperties
                ) );

                // if referenced item is a new none, delete it (and it's references) also
                if ( chainReferenceDelete && otherItemId < 0 ) {
                    dispatch( deleteItem(
                        dataContext,
                        otherItemId,
                        deletePhysically,
                        additionalElusiveDataContexts,
                        deleteProperties,
                        deleteReferences
                    ) );
                }
            }
        }

        // set status of existing items
        if ( item.id > 0 ) {
            const historyStatusId                     = metaModel[ item.schema ].getStatus( 'history' )!.id,
                  deletedStatusId                     = metaModel[ item.schema ].getStatus( 'deleted' )!.id,
                  statusId                            = deletePhysically ? deletedStatusId : historyStatusId,
                  attributes : ItemAttributesSettable = {
                      statusId
                  };

            if ( deleteProperties ) {
                if ( item.properties.size ) {
                    attributes.properties = {};

                    for ( const propertyTypeId of item.properties.keys() ) {
                        attributes.properties![ String( propertyTypeId ) ] = {
                            statusId
                        };
                    }
                }

                if ( item.enumProperties.size ) {
                    attributes.enumProperties = {};

                    for ( const enumProperty of item.enumProperties.entries() ) {
                        const propertyTypeIdString = String( enumProperty[ 0 ] );

                        attributes.enumProperties![ propertyTypeIdString ] = {
                            properties: {}
                        };

                        for ( const property of enumProperty[ 1 ].properties.values() ) {
                            attributes.enumProperties![ propertyTypeIdString ].properties![ property.stringKey ] = {
                                statusId
                            };
                        }
                    }
                }
            }

            item = item.reset().clone( attributes );
        }
        // delete newly added items
        else {
            global.data[ dataContext ].items[ itemId ].delete();
        }

        // update ElusiveData
        additionalElusiveDataContexts.unshift( dataContext );
        for ( const elusiveDataContext of additionalElusiveDataContexts ) {
            const elusiveData    = getState().elusiveData?.[ elusiveDataContext ],
                  newElusiveData = {
                      ...elusiveData,
                      data: {
                          ...elusiveData?.data,
                          items     : {
                              ...elusiveData?.data?.items
                          },
                          references: elusiveData?.data?.references ?? {}
                      }
                  };

            if ( item.id > 0 ) {
                newElusiveData.data.items[ item.id ] = ( elusiveData?.data?.items?.[ item.id ] ?? 0 ) + 1;
            }
            else {
                delete newElusiveData.data.items[ item.id ];
            }

            dispatch( setElusiveData( elusiveDataContext, newElusiveData ) );
        }
    }
}
export type DeleteItem = typeof deleteItem;

export default deleteItem;
